import { useEffect, useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Send, Sparkles, Users, Mail } from "lucide-react";

export const PresentationDialog = ({ building, open, onClose, onSent, resend = false }) => {
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState({}); // id -> bool
  const [html, setHtml] = useState("");
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const debounceRef = useRef(null);

  const load = useCallback(async () => {
    if (!building?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/buildings/${building.id}/presentation`);
      const d = r.data || {};
      setSubject(d.default_subject || "");
      setIntro(d.default_intro || "");
      const list = resend ? (d.new_recipients || []) : (d.recipients || []);
      setRecipients(list);
      setSelected(Object.fromEntries(list.map((x) => [x.id, true])));
      setHtml(d.html || "");
    } catch (err) {
      console.error(err);
      toast.error("Impossibile caricare la presentazione");
    } finally { setLoading(false); }
  }, [building?.id, resend]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Anteprima live dell'email (debounce sull'introduzione).
  useEffect(() => {
    if (!open || loading || !building?.id) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewing(true);
      try {
        const r = await api.post(`/buildings/${building.id}/presentation/preview`, {
          intro, origin_url: window.location.origin,
        });
        setHtml(r.data?.html || "");
      } catch { /* mantieni anteprima precedente */ }
      finally { setPreviewing(false); }
    }, 500);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intro, open, loading]);

  const selectedIds = recipients.filter((r) => selected[r.id]).map((r) => r.id);
  const allSelected = recipients.length > 0 && selectedIds.length === recipients.length;
  const toggleAll = () =>
    setSelected(Object.fromEntries(recipients.map((r) => [r.id, !allSelected])));

  const send = async () => {
    if (selectedIds.length === 0) { toast.error("Seleziona almeno un condòmino"); return; }
    setSending(true);
    try {
      const url = resend
        ? `/buildings/${building.id}/presentation/resend`
        : `/buildings/${building.id}/presentation`;
      const r = await api.post(url, {
        subject,
        intro,
        recipient_ids: allSelected && !resend ? null : selectedIds,
        origin_url: window.location.origin,
      });
      const n = r.data?.sent_count ?? selectedIds.length;
      toast.success(resend
        ? `Presentazione reinviata a ${n} nuovi condòmini`
        : `Presentazione inviata a ${n} condòmini`);
      onSent?.({
        presentation_sent_at: r.data?.sent_at || building?.presentation_sent_at,
        presentation_sent_count: r.data?.sent_count,
        resent: resend,
      });
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio non riuscito");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-[1180px] w-[95vw] max-h-[94vh] flex flex-col p-0 gap-0" data-testid="presentation-dialog">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#007BFF]" />
            {resend ? "Reinvia ai nuovi condòmini" : "Presenta Sedio ai condòmini"}
          </DialogTitle>
          <DialogDescription>
            {building?.name} · {resend
              ? "Invia la presentazione solo ai condòmini aggiunti dopo il primo invio."
              : "Rivedi e personalizza l'email, poi invia la presentazione ai condòmini."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />Caricamento…
          </div>
        ) : (
          <div className="grid md:grid-cols-5 gap-0 flex-1 min-h-0">
            {/* Colonna controlli */}
            <div className="md:col-span-2 p-6 overflow-y-auto space-y-5 border-r border-border">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 block">Oggetto</label>
                <Input data-testid="presentation-subject" value={subject}
                  onChange={(e) => setSubject(e.target.value)} className="text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 block">Messaggio introduttivo</label>
                <textarea data-testid="presentation-intro" value={intro}
                  onChange={(e) => setIntro(e.target.value)} rows={5}
                  className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#007BFF] resize-y" />
                <p className="text-[11px] text-muted-foreground mt-1">L'elenco delle funzioni e la grafica restano fissi. Puoi modificare solo questo messaggio.</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" />Destinatari ({selectedIds.length}/{recipients.length})
                  </label>
                  {recipients.length > 0 && (
                    <button type="button" data-testid="presentation-toggle-all"
                      onClick={toggleAll} className="text-xs text-[#007BFF] hover:underline">
                      {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
                    </button>
                  )}
                </div>
                {recipients.length === 0 ? (
                  <div className="text-sm text-muted-foreground bg-secondary/50 rounded-md p-3 flex items-center gap-2">
                    <Mail className="h-4 w-4" />{resend
                      ? "Nessun nuovo condòmino con email da contattare."
                      : "Nessun condòmino con email valida in anagrafica."}
                  </div>
                ) : (
                  <div className="border border-border rounded-md divide-y divide-border max-h-56 overflow-y-auto">
                    {recipients.map((r) => (
                      <label key={r.id} data-testid={`presentation-recipient-${r.id}`}
                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-secondary/50">
                        <input type="checkbox" checked={!!selected[r.id]}
                          onChange={() => setSelected((s) => ({ ...s, [r.id]: !s[r.id] }))}
                          className="h-4 w-4 accent-[#007BFF]" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{r.nome || "—"}</div>
                          <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Colonna anteprima */}
            <div className="md:col-span-3 bg-secondary/30 p-4 overflow-y-auto">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Anteprima email {previewing && <Loader2 className="h-3 w-3 animate-spin" />}
              </div>
              <div className="rounded-md border border-border bg-white overflow-hidden">
                <iframe data-testid="presentation-preview-frame" title="Anteprima presentazione"
                  srcDoc={html} className="w-full" style={{ height: 780, border: "none" }} />
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="px-6 py-4 border-t border-border shrink-0">
          <Button variant="outline" onClick={onClose} data-testid="presentation-cancel" disabled={sending}>Annulla</Button>
          <Button onClick={send} disabled={sending || loading || selectedIds.length === 0}
            data-testid="presentation-send" className="bg-[#007BFF] hover:bg-[#0069D9]">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            {resend ? "Reinvia ai nuovi" : "Invia presentazione"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
